/**
 * Frontend Input Sanitization Utilities
 * 
 * These functions provide client-side sanitization as a first line of defense.
 * The backend also sanitizes all input, so this is defense-in-depth.
 */

/**
 * Sanitize text input by removing HTML tags and limiting length.
 * @param {string} text - The text to sanitize
 * @param {number} maxLength - Maximum allowed length (default 1000)
 * @returns {string} Sanitized text
 */
export function sanitizeText(text, maxLength = 1000) {
  if (text === null || text === undefined) {
    return '';
  }
  
  // Convert to string if not already
  let clean = String(text);
  
  // Remove HTML tags (basic XSS prevention)
  clean = clean.replace(/<[^>]*>/g, '');
  
  // Decode HTML entities that might be used for XSS
  clean = clean
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/');
  
  // Remove the decoded tags again
  clean = clean.replace(/<[^>]*>/g, '');
  
  // Normalize whitespace (collapse multiple spaces into one)
  clean = clean.replace(/\s+/g, ' ');
  
  // Trim and limit length
  return clean.trim().slice(0, maxLength);
}

/**
 * Sanitize an object's string values.
 * @param {Object} obj - Object with string values to sanitize
 * @param {Object} fieldLengths - Map of field names to max lengths
 * @returns {Object} Object with sanitized values
 */
export function sanitizeObject(obj, fieldLengths = {}) {
  const defaultLength = 1000;
  const result = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      const maxLen = fieldLengths[key] || defaultLength;
      result[key] = sanitizeText(value, maxLen);
    } else {
      result[key] = value;
    }
  }
  
  return result;
}

/**
 * Validate quiz ID format (32 alphanumeric characters).
 * @param {string} quizId - The quiz ID to validate
 * @returns {boolean} True if valid
 */
export function isValidQuizId(quizId) {
  if (!quizId || typeof quizId !== 'string') {
    return false;
  }
  return /^[a-zA-Z0-9]{32}$/.test(quizId);
}

/**
 * Validate that a correct answer is A, B, C, or D.
 * @param {string} answer - The answer to validate
 * @returns {boolean} True if valid
 */
export function isValidAnswer(answer) {
  return ['A', 'B', 'C', 'D'].includes(answer?.toUpperCase?.());
}

/**
 * Field length limits (should match backend)
 */
export const FIELD_LIMITS = {
  title: 200,
  description: 1000,
  question: 2000,
  option_a: 500,
  option_b: 500,
  option_c: 500,
  option_d: 500
};
